// Library methods
import { useCallback, useContext, useMemo } from "react";

// Components
import Dash from "src/assets/shapes/Dash/Dash";
import ChartCross from "src/assets/shapes/ChartCross/ChartCross";
import Skeleton from "@mui/material/Skeleton";

// Contexts
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Constants
import { eyeTypeEnums, gridTypeEnums } from "src/constants/enums";

// Types
import { ProcessedPointType } from "src/services/exams/types";

const DEFAULT_CONTAINER_SIZE = 312;

type NumericalGraphType = {
  processedPoints: Array<ProcessedPointType>;
  gridType: string | undefined;
  isTerminated: boolean;
  containerSize?: number;
  isTotal?: boolean;
};

const NumericalGraph = ({
  processedPoints,
  gridType,
  isTerminated,
  containerSize = DEFAULT_CONTAINER_SIZE,
  isTotal = false,
}: NumericalGraphType) => {
  const { eyeType } = useContext(ReportSectionContext);
  const { RIGHT, LEFT } = eyeTypeEnums;

  const size = useMemo(() => containerSize * 0.9, [containerSize]);
  const tickSize = useMemo(() => size / 104, [size]);
  const scaleFactor = useMemo(() => size / DEFAULT_CONTAINER_SIZE, [size]);

  const is24C = useMemo(
    () => gridType === gridTypeEnums.G_24_2_C.type,
    [gridType]
  );

  const calculateXPaddingNumberGraph = useCallback((xFloor: number) => {
    // jumpAxis lets us jump from negative x axis to positive x axis (half of total width + line width + padding)
    const jumpAxis = 137;

    if (xFloor <= 0) {
      return 163 - xFloor * -31;
    } else {
      return xFloor * 31 + jumpAxis;
    }
  }, []);

  const calculateYPaddingNumberGraph = useCallback((yFloor: number) => {
    // jumpAxis lets us jump from negative y axis to negative y axis (half of total width + line width + padding)
    const jumpAxis = 135;

    if (yFloor >= 0) {
      return 160 - yFloor * 29;
    } else {
      return jumpAxis + yFloor * -29;
    }
  }, []);

  const shouldIgnoreValue = useCallback(
    (spot: { x: number; y: number }) =>
      gridType !== gridTypeEnums.G_10_2.type &&
      ((eyeType === LEFT && spot.x === -3 && (spot.y === -1 || spot.y === 1)) ||
        (eyeType === RIGHT && spot.x === 3 && (spot.y === -1 || spot.y === 1))),
    [gridType, eyeType, LEFT, RIGHT]
  );

  const getPaddingLeft = useCallback(
    (spot: { x: number; y: number }, ml: number) => {
      let value = ml - 8;

      if (is24C && 0 < spot.x && spot.x < 1) {
        value = ml + 6;
      } else if (is24C && eyeType === LEFT && spot.x === -1 && spot.y === -2) {
        value = ml - 10;
      } else if (is24C && eyeType === RIGHT && spot.x === 1 && spot.y === -2) {
        value = ml + 10;
      }

      return value * scaleFactor;
    },
    [is24C, LEFT, RIGHT, eyeType, scaleFactor]
  );

  const getPaddingTop = useCallback(
    (spot: { x: number; y: number }, mt: number) => {
      let value = mt;

      if (is24C && !Number.isInteger(spot.x) && spot.y === -2) {
        value = mt + 2;
      } else if (is24C && spot.y === -1.33) {
        value = mt + 6;
      }

      return value * scaleFactor;
    },
    [is24C, scaleFactor]
  );

  const getFontSize = useCallback(
    (spot: { x: number; y: number }) => {
      if (is24C && (!Number.isInteger(spot.x) || !Number.isInteger(spot.y))) {
        return "9px";
      }

      return "12px";
    },
    [is24C]
  );

  if (!processedPoints.length) {
    return (
      <Skeleton
        variant="rounded"
        width="100%"
        height={DEFAULT_CONTAINER_SIZE}
        sx={{
          borderRadius: "4px",
        }}
      />
    );
  }

  return (
    <main
      style={{
        height: `${size}px`,
        width: `${size}px`,
        position: "relative",
      }}
    >
      <ChartCross
        width={size}
        height={size}
        gridType={gridType}
        tickSize={tickSize}
      />

      {processedPoints.map((point, i: number) => {
        const spot = point?.Spot?.position;
        const shouldIgnore = shouldIgnoreValue(spot);

        const ml = calculateXPaddingNumberGraph(spot.x);
        const mt = calculateYPaddingNumberGraph(spot.y);

        if (shouldIgnore) return null;

        return (
          <p
            key={i}
            style={{
              position: "absolute",
              marginTop: "0px",
              paddingLeft: getPaddingLeft(spot, ml),
              paddingTop: getPaddingTop(spot, mt),
              fontSize: getFontSize(spot),
            }}
          >
            {isTerminated ? (
              <Dash size={8} />
            ) : (
              Math.floor(
                isTotal ? point?.TotalDeviation : point?.PatternDeviation
              )
            )}
          </p>
        );
      })}
    </main>
  );
};

export default NumericalGraph;
